import { BarChart, Button, Campaign, Checkout, Prizes, Ranking, Tag, defaultImage } from '@hub/shared'
import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import ModalCheckoutPrizeComponent from '../modal-checkout-prize/modal-checkout-prize.component'
import './item-to-checkout.component.scoped.scss'

interface Props extends React.HTMLAttributes<HTMLDivElement> {
  prize: Prizes
  userRanking: Ranking
  checkouts: Checkout[]
  campaign: Campaign
  onConfirmCheckout: () => void
}

export default ({ className = '', style = {}, prize, userRanking, checkouts = [], campaign, onConfirmCheckout }: Props) => {
  const [t] = useTranslation('engage')
  const [openModalCheckout, setOpenModalCheckout] = useState(false)

  if (!prize) {
    return null
  }

  const colorBar = useCallback(() => {
    if (prize?.soldOut) {
      return 'var(--red)'
    }

    const foundPrizeOnCheckouts = checkouts.find((checkout) => checkout?.prize?._id === prize?._id)
    if (userRanking?.gamification?.checkout?.total) {
      if (foundPrizeOnCheckouts) {
        return 'var(--red)'
      }

      return 'var(--red)'
    }

    if (userRanking?.totalAvailable < prize.points) {
      return 'var(--yellow)'
    }

    return 'var(--green)'
  }, [userRanking, prize, checkouts, campaign])

  const renderDescription = useCallback(() => {
    const foundPrizeOnCheckouts = checkouts.find((checkout) => checkout?.prize?._id === prize?._id)
    if (userRanking?.gamification?.checkout?.total) {
      if (foundPrizeOnCheckouts) {
        return 'alredyRequested'
      }

      return 'alredtRequestedAll'
    }

    if (foundPrizeOnCheckouts) {
      return 'alredtRequestedAll'
    }

    if (prize?.soldOut) {
      return 'soldout'
    }

    if (prize?.points > userRanking?.totalAvailable || !userRanking?.totalAvailable) {
      return 'cant'
    }

    return 'can'
  }, [prize, userRanking, checkouts, campaign])

  const renderActions = useCallback(() => {
    const foundPrizeOnCheckouts = checkouts.find((checkout) => checkout?.prize?._id === prize?._id)
    if (userRanking?.gamification?.checkout?.total) {
      return foundPrizeOnCheckouts ? (
        <Tag
          labels={[
            <div>
              <i className="fa-solid fa-circle-exclamation"></i> {t('prizesPage.statusButton.alredyRequested')}
            </div>,
          ]}
        />
      ) : (
        <Tag
          labels={[
            <div>
              <i className="fa-solid fa-circle-exclamation"></i> {t('prizesPage.statusButton.alredtRequestedAll')}
            </div>,
          ]}
        />
      )
    }

    if (foundPrizeOnCheckouts) {
      return (
        <Tag
          labels={[
            <div>
              <i className="fa-solid fa-circle-exclamation"></i> {t('prizesPage.statusButton.alredyRequested')}
            </div>,
          ]}
        />
      )
    }

    if (prize?.soldOut) {
      return (
        <Tag
          labels={[
            <div>
              <i className="fa-solid fa-circle-xmark red"></i> {t('prizesPage.statusButton.soldout')}
            </div>,
          ]}
        />
      )
    }

    return (
      <Button
        onClick={() => setOpenModalCheckout(true)}
        disabled={userRanking?.totalAvailable < prize.points || !userRanking?.totalAvailable}
        size="sm"
      >
        {t('prizesPage.statusButton.default')}
      </Button>
    )
  }, [prize, userRanking, checkouts, campaign, t, setOpenModalCheckout])

  return (
    <div className={`item-to-checkout ${className}`} style={style}>
      <header>
        <figure>
          <img
            src={prize?.image?.thumbnail}
            className="img-fluid"
            title={prize?.value}
            alt={prize?.value}
            onError={({ currentTarget }) => {
              currentTarget.onerror = null
              currentTarget.src = defaultImage
            }}
          />
        </figure>
        <div className="texts">
          <h6>{prize?.value}</h6>
          <div className="tags">
            <Tag labels={[`${prize?.points} ${t('points')}`]} separator />
          </div>
        </div>
        <div className="actions">{renderActions()}</div>
      </header>
      <div className="description">
        <p
          dangerouslySetInnerHTML={{
            __html: t(`prizesPage.status.${renderDescription()}`, { points: prize?.points - (userRanking?.totalAvailable || 0) }),
          }}
        />
      </div>
      {renderDescription() === 'can' || renderDescription() === 'cant' ? (
        <footer className="pb-5 mt-5">
          <BarChart
            colorBar={colorBar()}
            value={userRanking?.totalAvailable >= prize.points ? prize.points : userRanking?.totalAvailable}
            percentage={false}
            total={prize.points}
            labelPin={`${t('points')} `}
          />
        </footer>
      ) : null}
      <ModalCheckoutPrizeComponent
        onCloseChange={() => setOpenModalCheckout(false)}
        campaign={campaign}
        onOpenChange={(newOpen: boolean) => setOpenModalCheckout(newOpen)}
        onConfirmCheckout={() => {
          onConfirmCheckout()
          setOpenModalCheckout(false)
        }}
        open={openModalCheckout}
        prize={prize}
        userRanking={userRanking}
      />
    </div>
  )
}
